/**
 * Block Style Utilities
 * 區塊樣式工具函數：處理背景和邊框樣式的生成和應用
 */

/**
 * 獲取區塊樣式，未設定則返回預設值
 * @param {Object} pageDesign - 頁面設計物件
 * @param {string} blockId - 區塊 ID (例如 'bio', 'photos', 'links')
 * @returns {Object} 區塊樣式設定
 */
export function getBlockStyle(pageDesign, blockId) {
  return pageDesign?.block_styles?.[blockId] || {
    background: { type: 'solid', color: '#ffffff' },
    border: { width: '0px', color: '#e5e7eb', style: 'solid', radius: '16px' }
  };
}

/**
 * 根據樣式設定生成完整的 CSS style object
 * @param {Object} blockStyle - 區塊樣式設定 { background: {...}, border: {...} }
 * @returns {Object} CSS style object
 */
export function generateBlockStyle(blockStyle) {
  const style = {};

  // 背景樣式
  if (blockStyle.background?.type === 'transparent') {
    style.backgroundColor = 'transparent';
  } else if (blockStyle.background?.type === 'solid') {
    style.backgroundColor = blockStyle.background.color || '#ffffff';
  } else {
    // 預設白色
    style.backgroundColor = '#ffffff';
  }

  // 邊框樣式
  const border = blockStyle.border || {};
  const width = border.width || '0px';

  if (width !== '0px') {
    style.borderWidth = width;
    style.borderColor = border.color || '#e5e7eb';
    style.borderStyle = border.style || 'solid';
  }

  // 圓角
  style.borderRadius = border.radius || '16px';

  return style;
}

/**
 * 獲取預設區塊樣式（用於新建區塊時）
 * @returns {Object} 預設樣式設定
 */
export function getDefaultBlockStyle() {
  return {
    background: { type: 'solid', color: '#ffffff' },
    border: { width: '0px', color: '#e5e7eb', style: 'solid', radius: '16px' }
  };
}

// ==================== Text Style Utilities ====================

/**
 * 獲取預設文字樣式
 * @param {string} type - 'title' or 'content'
 * @returns {Object} 預設文字樣式設定
 */
export function getDefaultTextStyle(type = 'content') {
  const defaults = {
    title: {
      fontSize: 'text-2xl',
      fontFamily: '',
      fontWeight: 'font-bold',
      textColor: '#111827'
    },
    content: {
      fontSize: 'text-base',
      fontFamily: '',
      fontWeight: 'font-normal',
      textColor: '#374151'
    }
  };
  return defaults[type] || defaults.content;
}

/**
 * 獲取區塊的文字樣式（標題或內容）
 * @param {Object} blockStyle - 區塊樣式設定
 * @param {string} type - 'title' or 'content'
 * @returns {Object} 文字樣式設定
 */
export function getTextStyle(blockStyle, type = 'content') {
  if (!blockStyle?.textStyles?.[type]) {
    return getDefaultTextStyle(type);
  }

  // Merge with defaults to ensure all keys exist
  return {
    ...getDefaultTextStyle(type),
    ...blockStyle.textStyles[type]
  };
}

/**
 * 生成文字樣式的 CSS classes
 * @param {Object} textStyle - 文字樣式設定
 * @param {Object} pageDesign - 頁面設計物件（用於獲取全域預設字型）
 * @returns {string} CSS classes 字串
 */
export function generateTextStyleClasses(textStyle, pageDesign = null) {
  if (!textStyle) {
    return '';
  }

  // 字型家族處理：優先使用頁面全域字型，除非明確設定了特定字型
  let fontFamily;
  if (textStyle.fontFamily && textStyle.fontFamily !== '') {
    // 有明確設定特定字型（非空字串）
    fontFamily = textStyle.fontFamily;
  } else {
    // 空字串或 undefined/null = 使用頁面預設字型
    fontFamily = pageDesign?.styles?.font_family || 'font-sans';
  }

  const classes = [
    textStyle.fontSize || 'text-base',
    fontFamily,
    textStyle.fontWeight || 'font-normal'
  ];

  return classes.filter(Boolean).join(' ');
}

/**
 * 生成文字樣式的 inline styles（用於顏色）
 * @param {Object} textStyle - 文字樣式設定
 * @returns {Object} CSS style object
 */
export function generateTextStyleInlineStyles(textStyle) {
  if (!textStyle) return {};

  return {
    color: textStyle.textColor || '#374151'
  };
}
